import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { useForm } from "react-hook-form";
import { number, object, preprocess, string, infer as infer_ } from "zod";

import { NumberInputFormField } from "src/components/Form/NumberInputFormField";
import { TextAreaFormField } from "src/components/Form/TextAreaFormField";
import { TextInputFormField } from "src/components/Form/TextInputFormField";
import { useT } from "src/internationalization";
import { useFormatDate } from "src/internationalization/formatters";
import { ScenarioVersion } from "src/types/scenario.ts";

export type EditScenarioVersionFormRef = {
  submit: (onSubmit: (data: ValidatedFormState) => void, onError: () => void) => () => void;
};

export type EditScenarioVersionFormProps = {
  draftVersion: ScenarioVersion;
  onValidChange: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

type FormState = {
  name: string;
  threshold: string;
  description: string;
};

type ValidatedFormState = infer_<ReturnType<typeof useSchema>>;

export const EditScenarioVersionForm = forwardRef<
  EditScenarioVersionFormRef,
  EditScenarioVersionFormProps
>(function EditScenarioVersionForm({ draftVersion, onValidChange, onDirtyChange }, ref) {
  const formatDate = useFormatDate();
  const t = useT();
  const validationSchema = useSchema();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted, isDirty },
  } = useForm<FormState>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: draftVersion.name,
      threshold: draftVersion.threshold.toString(),
      description: draftVersion.description ?? "",
    },
  });

  useEffect(() => {
    onValidChange(isSubmitted ? isValid : true);
    // We do not want to fire this effect when onValidChange changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useImperativeHandle(ref, () => {
    return {
      submit: (onSubmit, onError) =>
        handleSubmit((data) => {
          const validatedData = data as unknown as ValidatedFormState;
          onSubmit(validatedData);
        }, onError),
    };
  });

  return (
    <div className="flex flex-col gap-7">
      <div className="text-textPrimary text-xl">
        {t("edit.scenario.version.modal.version.tab.title")}
      </div>
      <div className="flex flex-col gap-5">
        <TextInputFormField
          id="versionName"
          control={control}
          name="name"
          placeholder={t("general.form.placeholder.example", {
            example: formatDate(new Date(), {
              year: "numeric",
              month: "long",
            }),
          })}
          label={t("general.form.name.label")}
          helperText={t("scenarios.version.form.name.helper")}
        />
        <NumberInputFormField
          id="versionThreshold"
          control={control}
          name="threshold"
          min={1}
          placeholder={t("general.form.placeholder.example", { example: "100" })}
          label={t("scenarios.form.threshold.label")}
          helperText={t("scenarios.form.threshold.helper")}
        />
        <TextAreaFormField
          id="versionDescription"
          control={control}
          name="description"
          label={t("general.form.description.label")}
          helperText={t("scenarios.version.form.description.helper")}
        />
      </div>
    </div>
  );
});

const useSchema = () => {
  const t = useT();

  return useMemo(() => {
    return object({
      name: string().min(1, t("general.form.name.error.required")),
      threshold: preprocess(
        (value) => {
          if (typeof value === "string") {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? undefined : parsed;
          }
          return value;
        },
        number({
          message: t("scenarios.form.threshold.error.required"),
        }).min(0, t("scenarios.form.threshold.error.min", { min: 0 }))
      ),
      description: string().min(1, t("general.form.description.error.required")),
    });
  }, [t]);
};
